import React, { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

/* ── Validators ── */
const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

function isValidEmail(e) {
  return EMAIL_RE.test(e.trim());
}

function isValidPhone(p) {
  return p.replace(/\D/g, "").length === 10;
}

/* ── US phone auto-formatter: (###) ###-#### ── */
function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/* ── Field component ── */
function Field({ label, children, hint, error }) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: "10px",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: error ? "rgba(255,80,80,0.85)" : "rgba(255,255,255,0.40)",
          marginBottom: "10px",
          transition: "color 0.2s",
        }}
      >
        {label}
      </label>
      {children}
      {(hint || error) && (
        <p
          style={{
            fontSize: "11px",
            marginTop: "6px",
            color: error ? "rgba(255,100,80,0.80)" : "rgba(255,255,255,0.30)",
          }}
        >
          {error || hint}
        </p>
      )}
    </div>
  );
}

export default function ContactStep({ value, onChange, onNext, onBack }) {
  const [firstName, setFirstName] = useState(value?.firstName || "");
  const [lastName,  setLastName]  = useState(value?.lastName  || "");
  const [email,     setEmail]     = useState(value?.email     || "");
  const [phone,     setPhone]     = useState(value?.phone     || "");

  /* Inline error states (shown after first blur) */
  const [touched, setTouched] = useState({
    firstName: false,
    lastName:  false,
    email:     false,
    phone:     false,
  });

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const errors = {
    firstName: touched.firstName && firstName.trim().length < 2
      ? "Enter your first name" : "",
    lastName:  touched.lastName  && lastName.trim().length < 2
      ? "Enter your last name"  : "",
    email:     touched.email && !isValidEmail(email)
      ? "Enter a valid email address" : "",
    phone:     touched.phone && !isValidPhone(phone)
      ? "Enter a 10-digit US phone number" : "",
  };

  const submit = () => {
    setTouched({ firstName: true, lastName: true, email: true, phone: true });

    if (firstName.trim().length < 2) return toast.error("Enter your first name");
    if (lastName.trim().length < 2)  return toast.error("Enter your last name");
    if (!isValidEmail(email))         return toast.error("Enter a valid email address");
    if (!isValidPhone(phone))         return toast.error("Enter a 10-digit US phone number");

    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    onChange({ name: fullName, firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), phone });
    onNext();
  };

  return (
    <div data-testid="step-contact">
      {/* Heading */}
      <h2
        style={{
          fontFamily: "'Geist', sans-serif",
          fontWeight: 600,
          fontSize: "clamp(1.9rem, 6vw, 2.8rem)",
          letterSpacing: "-0.04em",
          lineHeight: 0.95,
          color: "#f5f5f2",
          marginBottom: "12px",
        }}
      >
        How do we{" "}
        <em
          style={{
            fontStyle: "italic",
            background:
              "linear-gradient(135deg, #8A8A8A 0%, #AAAAAA 50%, #8A8A8A 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          reach you?
        </em>
      </h2>
      <p
        style={{
          color: "rgba(255,255,255,0.50)",
          fontSize: "14px",
          lineHeight: 1.65,
          marginBottom: "28px",
        }}
      >
        Booking confirmation goes to your email. We'll call if anything changes.
      </p>

      <motion.div
        style={{ display: "flex", flexDirection: "column", gap: "18px" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* First + Last name row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <Field label="First Name" error={errors.firstName}>
            <input
              className="input-luxe"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => touch("firstName")}
              placeholder="Jane"
              data-testid="contact-firstname-input"
              autoComplete="given-name"
            />
          </Field>
          <Field label="Last Name" error={errors.lastName}>
            <input
              className="input-luxe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => touch("lastName")}
              placeholder="Smith"
              data-testid="contact-lastname-input"
              autoComplete="family-name"
            />
          </Field>
        </div>

        {/* Email */}
        <Field
          label="Email"
          error={errors.email}
          hint={!errors.email && touched.email && email ? "" : ""}
        >
          <input
            type="email"
            className="input-luxe"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => touch("email")}
            placeholder="jane@example.com"
            data-testid="contact-email-input"
            autoComplete="email"
            inputMode="email"
          />
        </Field>

        {/* Phone */}
        <Field
          label="Phone (US)"
          error={errors.phone}
          hint={!errors.phone ? "Format: (518) 555-0123" : ""}
        >
          <input
            type="tel"
            className="input-luxe"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            onBlur={() => touch("phone")}
            placeholder="(518) 555-0123"
            data-testid="contact-phone-input"
            autoComplete="tel"
            inputMode="tel"
          />
        </Field>
      </motion.div>

      <div
        style={{
          marginTop: "36px",
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <button
          className="btn-secondary"
          onClick={onBack}
          data-testid="contact-back-button"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          className="btn-primary"
          onClick={submit}
          data-testid="contact-next-button"
        >
          Continue
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
